function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, Fragment } from "react";
import { cva } from "class-variance-authority";
import { Button } from "../Button/index.js";
import { Container as Root } from "../Container/index.js";
import { Typography } from "../Typography/index.js";
import { cn } from "../../common/helpers.js";
const containerClasses = cva([
    'relative',
    'py-12',
    'md:py-20',
    'px-5',
    'md:px-10'
], {
    variants: {
        containerSize: {
            12: [
                'col-span-12'
            ],
            10: [
                'col-span-12',
                'lg:col-span-10',
                'lg:col-start-2'
            ],
            8: [
                'col-span-12',
                'lg:col-span-10',
                'lg:col-start-2',
                'xl:col-span-8',
                'xl:col-start-3'
            ]
        }
    }
});
/**
 * CTA Background Block - [API](https://scl.sharp.com/?path=/docs/components-ctabackgroundblock) | [DSM](https://sharphealthcare.notion.site/CTA-background-ee1c5bf23821427c83ad31b9068a9238)
 *
 * @example
 * <CTABackgroundBlock
 *   heading="This is where the required heading goes"
 *   description="This is the description that follows and its optional and can include rich text."
 *   buttonPrimary={{
 *     label: 'Primary CTA',
 *   }}
 *   buttonSecondary={{
 *     label: 'Secondary CTA',
 *   }}
 *   image={{
 *     src: 'floral.webp',
 *     alt: 'Floral background color.',
 *   }}
 *  />
 *
 */ const CTABackgroundBlock = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { heading, description, buttonPrimary, buttonSecondary, containerSize = 12, image = {}, className, slots = {}, slotProps = {} } = _param, rest = _object_without_properties(_param, [
        "heading",
        "description",
        "buttonPrimary",
        "buttonSecondary",
        "containerSize",
        "image",
        "className",
        "slots",
        "slotProps"
    ]);
    var _slotProps_description;
    var _slots_image;
    const Image = (_slots_image = slots.image) !== null && _slots_image !== void 0 ? _slots_image : 'img';
    const { className: imagePropClassName } = image, imagePropProps = _object_without_properties(image, [
        "className"
    ]);
    var _slotProps_image;
    const _ref = (_slotProps_image = slotProps.image) !== null && _slotProps_image !== void 0 ? _slotProps_image : {}, { className: imageSlotPropsClassName } = _ref, imageSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    const imageProps = slots.image ? _object_spread({}, imageSlotProps) : _object_spread({}, imagePropProps, imageSlotProps);
    var _slots_heading;
    const Heading = (_slots_heading = slots.heading) !== null && _slots_heading !== void 0 ? _slots_heading : Typography;
    var _slotProps_heading;
    const _ref1 = (_slotProps_heading = slotProps.heading) !== null && _slotProps_heading !== void 0 ? _slotProps_heading : {}, { className: headingSlotPropsClassName } = _ref1, headingSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    // If heading slot provided then do not spread Typography specific props else combine
    const headingProps = slots.heading ? headingSlotProps : _object_spread({
        variant: 'h2',
        align: 'center'
    }, headingSlotProps);
    var _slots_description;
    const Description = (_slots_description = slots.description) !== null && _slots_description !== void 0 ? _slots_description : Typography;
    var _slotProps_description1;
    const _ref2 = (_slotProps_description1 = slotProps.description) !== null && _slotProps_description1 !== void 0 ? _slotProps_description1 : {}, { className: descriptionSlotPropsClassName } = _ref2, descriptionSlotProps = _object_without_properties(_ref2, [
        "className"
    ]);
    // If description slot provided then do not spread Typography specific props else combine
    const descriptionProps = slots.description ? descriptionSlotProps : _object_spread({
        variant: 'body',
        as: 'div',
        align: 'center'
    }, descriptionSlotProps);
    var _slots_buttonPrimaryContainer;
    const ButtonPrimaryContainer = (_slots_buttonPrimaryContainer = slots.buttonPrimaryContainer) !== null && _slots_buttonPrimaryContainer !== void 0 ? _slots_buttonPrimaryContainer : slotProps.buttonPrimaryContainer ? 'div' : Fragment;
    var _slots_buttonPrimary;
    const ButtonPrimary = (_slots_buttonPrimary = slots.buttonPrimary) !== null && _slots_buttonPrimary !== void 0 ? _slots_buttonPrimary : Button;
    const buttonPrimaryProps = slots.buttonPrimary ? slotProps.buttonPrimary : _object_spread({
        variant: 'filled'
    }, buttonPrimary, slotProps.buttonPrimary);
    var _slots_buttonSecondaryContainer;
    const ButtonSecondaryContainer = (_slots_buttonSecondaryContainer = slots.buttonSecondaryContainer) !== null && _slots_buttonSecondaryContainer !== void 0 ? _slots_buttonSecondaryContainer : slotProps.buttonSecondaryContainer ? 'div' : Fragment;
    var _slots_buttonSecondary;
    const ButtonSecondary = (_slots_buttonSecondary = slots.buttonSecondary) !== null && _slots_buttonSecondary !== void 0 ? _slots_buttonSecondary : Button;
    const buttonSecondaryProps = slots.buttonSecondary ? slotProps.buttonSecondary : _object_spread({
        variant: 'outlined'
    }, buttonSecondary, slotProps.buttonSecondary);
    return /*#__PURE__*/ _jsx(Root, _object_spread_props(_object_spread({
        ref: ref,
        fullWidth: "lg",
        className: cn('grid grid-cols-12', className)
    }, rest), {
        children: /*#__PURE__*/ _jsxs("div", {
            className: containerClasses({
                containerSize
            }),
            children: [
                /*#__PURE__*/ _jsx(Image, _object_spread({
                    className: cn(imagePropClassName, imageSlotPropsClassName, 'absolute inset-0 w-full h-full -z-10 pointer-events-none object-cover object-center lg:rounded-xl')
                }, imageProps)),
                /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-[860px] mx-auto",
                    children: [
                        /*#__PURE__*/ _jsx(Heading, _object_spread_props(_object_spread({
                            className: cn('line-clamp-3 md:line-clamp-2', headingSlotPropsClassName)
                        }, headingProps), {
                            children: heading
                        })),
                        (description !== null && description !== void 0 ? description : (_slotProps_description = slotProps.description) === null || _slotProps_description === void 0 ? void 0 : _slotProps_description.children) && /*#__PURE__*/ _jsx(Description, _object_spread_props(_object_spread({
                            className: cn('scl-rich-text mt-6', descriptionSlotPropsClassName)
                        }, descriptionProps), {
                            children: description
                        })),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col sm:flex-row justify-center align-middle gap-x-6 gap-y-5 mt-6",
                            children: [
                                /*#__PURE__*/ _jsx(ButtonPrimaryContainer, _object_spread_props(_object_spread({}, slotProps === null || slotProps === void 0 ? void 0 : slotProps.buttonPrimaryContainer), {
                                    children: /*#__PURE__*/ _jsx(ButtonPrimary, _object_spread({}, buttonPrimaryProps))
                                })),
                                (buttonSecondary || slots.buttonSecondary) && /*#__PURE__*/ _jsx(ButtonSecondaryContainer, _object_spread_props(_object_spread({}, slotProps === null || slotProps === void 0 ? void 0 : slotProps.buttonSecondaryContainer), {
                                    children: /*#__PURE__*/ _jsx(ButtonSecondary, _object_spread({}, buttonSecondaryProps))
                                }))
                            ]
                        })
                    ]
                })
            ]
        })
    }));
});
export { CTABackgroundBlock };
